import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Col, Row } from "react-bootstrap";
import Swal from "sweetalert2";

import { AppUserApi } from "../../../api";
import {
    AiGoalType,
    AppUserType,
    DailyUserSummaryType,
    PlanDayType,
    PlanWeekType,
    WeeklyUserSummaryType,
} from "../../../DTO";
import { useAppParams } from "../../../Router/hook";
import { formatUserId } from "../../../utils";
import { ActivityTargetTrace, CustomBreadCrumb } from "../../../components";
import { selectUser } from "../../../redux-config/entities/user";
import { useAppSelector } from "../../../redux-config/hooks";
import { HeaderCard } from "./components/header-card";
import { SelectorPanel } from "./components/selector-panel";
import { WeeklySummarySection } from "./components/weekly-summary-section";
import { DailySummarySection } from "./components/daily-summary-section";
import "./style.scss";

export const UserSummary: FC = () => {
    const { id } = useAppParams();
    const user = useAppSelector(selectUser);

    const userId = useMemo(() => Number(id), [id]);

    const [userContext, setUserContext] = useState<AppUserType>();
    const [loadingUserContext, setLoadingUserContext] = useState(false);

    const [currentDay, setCurrentDay] = useState("");
    const [selectedDayId, setSelectedDayId] = useState(0);
    const [selectedPlanDayId, setSelectedPlanDayId] = useState<number | null>(null);
    const [selectedWeek, setSelectedWeek] = useState<PlanWeekType | null>(null);
    const [hasWeekSelection, setHasWeekSelection] = useState(false);
    const [hasDaySelection, setHasDaySelection] = useState(false);

    const [goals, setGoals] = useState<AiGoalType[]>([]);

    const [weeklySummary, setWeeklySummary] = useState<WeeklyUserSummaryType>();
    const [loadingWeekly, setLoadingWeekly] = useState(false);
    const [weeklyError, setWeeklyError] = useState("");

    const [dailySummary, setDailySummary] = useState<DailyUserSummaryType>();
    const [loadingDaily, setLoadingDaily] = useState(false);
    const [dailyError, setDailyError] = useState("");

    const [activeTab, setActiveTab] = useState<"weekly" | "daily">("weekly");

    const crumbs = [
        { name: "", href: "" },
        { name: "Dashboard", href: "/" },
        { name: "User list", href: "/app-user" },
        { name: `Summary ${formatUserId(id)}`, href: "#" },
    ];

    const fetchUserContext = useCallback(() => {
        if (!userId) return;
        setLoadingUserContext(true);

        AppUserApi.ShowAppUser(user, userId)
            .then((data) => setUserContext(data.user))
            .catch((err) => {
                Swal.fire({
                    icon: "error",
                    text: err,
                });
            })
            .finally(() => setLoadingUserContext(false));
    }, [user, userId]);

    const fetchWeeklySummary = useCallback(() => {
        if (!selectedWeek?.id || !userId || !hasWeekSelection) return;

        setLoadingWeekly(true);
        setWeeklyError("");

        AppUserApi.getWeeklyUserSummary(user, userId, selectedWeek.id)
            .then((data) => setWeeklySummary(data.summary))
            .catch((err) => setWeeklyError(err))
            .finally(() => setLoadingWeekly(false));
    }, [user, userId, selectedWeek?.id, hasWeekSelection]);

    const fetchDailySummary = useCallback(() => {
        if (!selectedDayId || !userId) return;

        setLoadingDaily(true);
        setDailyError("");

        AppUserApi.getDailyUserSummary(user, userId, selectedDayId)
            .then((data) => setDailySummary(data.summary))
            .catch((err) => setDailyError(err))
            .finally(() => setLoadingDaily(false));
    }, [selectedDayId, user, userId]);

    const fetchGoals = useCallback(() => {
        if (!userId) return;
        AppUserApi.GetUserGoals(user, userId)
            .then((data) => setGoals(data.goals))
            .catch(() => {});
    }, [user, userId]);

    const weekGoal = useMemo(
        () => goals.find((g) => g.type === "week" && g.weekId === selectedWeek?.id) ?? null,
        [goals, selectedWeek?.id],
    );

    const dayGoal = useMemo(
        () => goals.find((g) => g.type === "day" && g.dayId === selectedPlanDayId) ?? null,
        [goals, selectedPlanDayId],
    );

    const handleGoalUpdated = useCallback((updated: AiGoalType) => {
        setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    }, []);

    const handleWeekSelect = useCallback((week: PlanWeekType) => {
        setHasWeekSelection(true);
        setSelectedWeek(week);
        setCurrentDay("");
        setSelectedDayId(0);
        setSelectedPlanDayId(null);
        setHasDaySelection(false);
        setDailySummary(undefined);
        setDailyError("");
    }, []);

    const handleDaySelect = useCallback((day: PlanDayType) => {
        setHasDaySelection(true);
        setSelectedDayId(day.dailyTaskId || day.dayId || day.id);
        setSelectedPlanDayId(day.dayId ?? day.id);
    }, []);

    useEffect(() => {
        if (!hasWeekSelection || !selectedWeek?.id) return;
        fetchWeeklySummary();
    }, [fetchWeeklySummary, hasWeekSelection, selectedWeek?.id]);

    useEffect(() => {
        if (!selectedDayId) return;
        fetchDailySummary();
    }, [fetchDailySummary, selectedDayId]);

    useEffect(() => {
        fetchUserContext();
    }, [fetchUserContext]);

    useEffect(() => {
        fetchGoals();
    }, [fetchGoals]);

    return (
        <>
            <Helmet>
                <title>D2Type | User Summary</title>
            </Helmet>

            <div className="summary-shell d-flex flex-column gap-3 my-3">
                <CustomBreadCrumb breadCrumbList={crumbs} />

                <HeaderCard
                    userId={userId}
                    userContext={userContext}
                    loadingUserContext={loadingUserContext}
                    selectedWeekIndex={hasWeekSelection ? selectedWeek?.index : null}
                />

                <ActivityTargetTrace
                    userId={userId}
                    compact={!hasWeekSelection}
                    selectedWeekIndex={hasWeekSelection ? selectedWeek?.index : undefined}
                />

                <Row className="g-3 align-items-start">
                    <Col xl={4}>
                        <div className="summary-sticky-rail">
                            <SelectorPanel
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                userId={userId}
                                hasWeekSelection={hasWeekSelection}
                                selectedWeek={selectedWeek}
                                currentDay={currentDay}
                                setCurrentDay={setCurrentDay}
                                onWeekSelect={handleWeekSelect}
                                onDaySelect={handleDaySelect}
                            />
                        </div>
                    </Col>

                    <Col xl={8}>
                        {activeTab === "weekly" && (
                            <WeeklySummarySection
                                hasWeekSelection={hasWeekSelection}
                                selectedWeekId={selectedWeek?.id}
                                loading={loadingWeekly}
                                error={weeklyError}
                                summary={weeklySummary}
                                onRetry={fetchWeeklySummary}
                                weekGoal={weekGoal}
                                onGoalUpdated={handleGoalUpdated}
                            />
                        )}

                        {activeTab === "daily" && (
                            <DailySummarySection
                                hasWeekSelection={hasWeekSelection}
                                hasDaySelection={hasDaySelection}
                                selectedDayId={selectedDayId}
                                loading={loadingDaily}
                                error={dailyError}
                                summary={dailySummary}
                                onRetry={fetchDailySummary}
                                dayGoal={dayGoal}
                                onGoalUpdated={handleGoalUpdated}
                            />
                        )}
                    </Col>
                </Row>
            </div>
        </>
    );
};
